import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { canWrite } from '../../rbac';
import { API_ORIGIN, complaintAPI } from '../../services/api';
import './AdminComplaintDetail.css';

const statusActions = {
  filed: [['acknowledged', 'स्वीकृत करा'], ['assigned', 'नेमणूक करा'], ['rejected', 'नाकारा']],
  acknowledged: [['assigned', 'नेमणूक करा'], ['rejected', 'नाकारा']],
  assigned: [['in_progress', 'प्रगतीपथावर'], ['rejected', 'नाकारा']],
  in_progress: [['resolved', 'निराकरण करा'], ['rejected', 'नाकारा']],
  resolved: [['verified', 'सत्यापित करा'], ['closed', 'बंद करा'], ['reopened', 'पुन्हा उघडा']],
  verified: [['closed', 'बंद करा']],
  closed: [['reopened', 'पुन्हा उघडा']],
  reopened: [['assigned', 'नेमणूक करा'], ['in_progress', 'प्रगतीपथावर']],
  rejected: [['reopened', 'पुन्हा उघडा']],
};

const dateText = value => value ? new Date(value).toLocaleDateString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const dateTimeText = value => value ? new Date(value).toLocaleString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const label = value => String(value || '—').replace(/_/g, ' ');

function getComplaintMedia(complaint) {
  if (!complaint) return [];
  let media = complaint.media_urls || [];
  if (typeof media === 'string') {
    try { media = JSON.parse(media); } catch { media = []; }
  }
  const list = Array.isArray(media) ? [...media] : [];
  if (!list.length && complaint.photo_before_url) {
    list.push({ url: complaint.photo_before_url, type: 'image', originalName: 'तक्रार फोटो' });
  }
  if (complaint.photo_after_url) {
    list.push({ url: complaint.photo_after_url, type: 'image', originalName: 'निराकरणानंतरचा फोटो' });
  }
  return list;
}

function mediaUrl(url) {
  if (!url) return '';
  return url.startsWith('http') ? url : `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
}

export default function AdminComplaintDetail() {
  const { complaintId } = useParams();
  const navigate = useNavigate();
  const { tenant, user } = useAuth();
  const slug = tenant?.slug;
  const canUpdate = canWrite(user?.role, 'complaints', user);
  const [complaint, setComplaint] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ notes: '', expected_resolution_date: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeMediaModal, setActiveMediaModal] = useState(null);

  const loadComplaint = useCallback(async () => {
    if (!slug || !complaintId) return;
    setLoading(true);
    setError('');
    try {
      const response = await complaintAPI.getById(slug, complaintId);
      const record = response.data.complaint;
      setComplaint(record);
      setHistory(response.data.history || []);
      setForm(current => ({ ...current, expected_resolution_date: record?.expected_resolution_date?.slice(0, 10) || '' }));
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'तक्रार तपशील लोड करता आला नाही.');
    } finally { setLoading(false); }
  }, [slug, complaintId]);

  useEffect(() => { loadComplaint(); }, [loadComplaint]);

  const updateStatus = async status => {
    setSaving(true);
    setError('');
    try {
      await complaintAPI.updateStatus(slug, complaintId, { status, resolution_notes: form.notes, expected_resolution_date: form.expected_resolution_date || null });
      setForm(current => ({ ...current, notes: '' }));
      await loadComplaint();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'तक्रार अद्ययावत करता आली नाही.');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="admin-card">लोड होत आहे...</div>;
  if (!complaint) return <div className="admin-card"><button className="btn btn-outline" onClick={() => navigate('../complaints')}>← मागे</button><p>{error || 'तक्रार उपलब्ध नाही.'}</p></div>;

  const media = getComplaintMedia(complaint);

  return <div className="complaint-detail-page">
    <div className="complaint-page-heading"><button type="button" onClick={() => navigate('../complaints')}>←</button><div><h1>तक्रार तपशील</h1><span>तक्रार क्रमांक: {complaint.complaint_uid}</span></div></div>
    {error && <div className="alert alert-error">{error}</div>}

    <section className="complaint-detail-hero">
      <div className="complaint-hero-icon">▤</div>
      <div className="complaint-hero-copy"><em>{label(complaint.severity)}</em><h2>{complaint.title}</h2><p>श्रेणी: {label(complaint.category)} · नोंद दिनांक: {dateText(complaint.createdAt || complaint.created_at)}</p></div>
      <div className="complaint-hero-status"><span>सध्याची स्थिती</span><strong>{label(complaint.status)}</strong><small>अंतिम निराकरण: {dateText(complaint.resolved_at)}</small></div>
    </section>

    <section className="complaint-summary-grid">
      <article><span>👤 तक्रारदार</span><strong>{complaint.filedByUser?.full_name || '—'}</strong><small>{complaint.filedByUser?.phone || ''}</small></article>
      <article><span>📍 स्थळ</span><strong>{complaint.location_address || '—'}</strong><small>{complaint.ward_number ? `प्रभाग ${complaint.ward_number}` : ''}</small></article>
      <article><span>👤 नेमणूक</span><strong>{complaint.assignedOfficer?.full_name || complaint.assigned_department || 'नेमणूक नाही'}</strong></article>
      <article><span>☷ वर्णन</span><p>{complaint.description || '—'}</p></article>
    </section>

    {/* Attachments & Proofs */}
    {media.length > 0 && (
      <section className="complaint-detail-panel complaint-media-panel">
        <h3>📎 जोडलेले पुरावे ({media.length})</h3>
        <div className="admin-complaint-media-grid">
          {media.map((item, index) => {
            const url = typeof item === 'string' ? item : item.url;
            const name = typeof item === 'string' ? `पुरावा ${index + 1}` : (item.originalName || item.fileName || `पुरावा ${index + 1}`);
            const isVideo = item.type === 'video' || item.mimeType?.startsWith('video/') || /\.(mp4|mov|webm|mkv)$/i.test(url);
            const isPdf = item.type === 'pdf' || item.mimeType === 'application/pdf' || /\.pdf$/i.test(url);
            const fullUrl = mediaUrl(url);

            return (
              <div
                key={`${url}-${index}`}
                className="admin-complaint-media-card"
                onClick={() => setActiveMediaModal({ url: fullUrl, name, isVideo, isPdf })}
                title="मोठा आकार पाहण्यासाठी क्लिक करा"
              >
                <div className="admin-complaint-media-thumb">
                  {isVideo ? (
                    <div className="admin-media-video-wrap">
                      <video src={fullUrl} preload="metadata" />
                      <span className="admin-media-play">▶</span>
                    </div>
                  ) : isPdf ? (
                    <div className="admin-media-pdf-wrap">
                      <span style={{ fontSize: '2.5rem' }}>📄</span>
                    </div>
                  ) : (
                    <img
                      src={fullUrl}
                      alt={name}
                      onError={(e) => { e.target.onerror = null; e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24"><text y="18" font-size="18">🖼️</text></svg>'; }}
                    />
                  )}
                  <div className="admin-media-hover-overlay">
                    <span>🔍 मोठे पाहा</span>
                  </div>
                </div>
                <div className="admin-complaint-media-caption">
                  <strong title={name}>{name}</strong>
                  <small>{isVideo ? 'व्हिडिओ' : isPdf ? 'दस्तऐवज' : 'फोटो'}</small>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    )}

    <div className="complaint-detail-columns">
      <section className="complaint-detail-panel"><h3>▣ कार्यवाही टाइमलाइन ({history.length})</h3><div className="complaint-timeline">
        {history.map((item, index) => <article key={item.id || index}><i /><div><header><strong>{item.action === 'created' ? 'तक्रार नोंद' : item.action === 'status_changed' ? 'स्थिती बदल' : item.action === 'due_date_changed' ? 'देय तारीख अद्ययावत' : 'नोंद जोडली'}</strong><time>{dateTimeText(item.created_at || item.createdAt)}</time></header><p><b>{item.changed_by_name}</b> द्वारे · {label(item.changed_by_role)}</p>{item.old_value && item.new_value && <p><mark>{label(item.old_value)}</mark> → <mark>{label(item.new_value)}</mark></p>}{item.notes && <blockquote>💬 {item.notes}</blockquote>}</div></article>)}
        {!history.length && <p>अद्याप कार्यवाही नोंद उपलब्ध नाही.</p>}
      </div></section>

      <aside><section className="complaint-detail-panel"><h3>▧ तक्रार माहिती</h3><dl><div><dt>तक्रार क्रमांक</dt><dd>{complaint.complaint_uid}</dd></div><div><dt>श्रेणी</dt><dd>{label(complaint.category)}</dd></div><div><dt>प्राधान्य</dt><dd>{label(complaint.severity)}</dd></div><div><dt>स्थिती</dt><dd>{label(complaint.status)}</dd></div><div><dt>अपेक्षित निराकरण</dt><dd>{dateText(complaint.expected_resolution_date)}</dd></div></dl></section>
      {complaint.resolution_notes && <section className="complaint-detail-panel resolution"><h3>✓ कारवाईचा सारांश</h3><p>{complaint.resolution_notes}</p></section>}</aside>
    </div>

    {canUpdate && <section className="complaint-action-panel"><h3>⚡ कार्यवाही करा</h3><div><label>नोंद जोडा<textarea rows="3" value={form.notes} onChange={event => setForm({ ...form, notes: event.target.value })} /></label><label>अपेक्षित निराकरण तारीख<input type="date" value={form.expected_resolution_date} onChange={event => setForm({ ...form, expected_resolution_date: event.target.value })} /></label></div><footer>{(statusActions[complaint.status] || []).map(([status, text]) => <button disabled={saving} key={status} className="btn btn-primary" onClick={() => updateStatus(status)}>{text}</button>)}</footer></section>}

    {/* Lightbox / Media Viewer Modal */}
    {activeMediaModal && (
      <div
        className="admin-media-modal-backdrop"
        onClick={() => setActiveMediaModal(null)}
        role="dialog"
        aria-modal="true"
      >
        <div className="admin-media-modal-content" onClick={e => e.stopPropagation()}>
          <div className="admin-media-modal-header">
            <div>
              <h4>{activeMediaModal.name}</h4>
              <span style={{ fontSize: '0.78rem', color: '#65718c' }}>
                {activeMediaModal.isVideo ? 'व्हिडिओ' : activeMediaModal.isPdf ? 'दस्तऐवज' : 'छायाचित्र'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <a
                href={activeMediaModal.url}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline"
                style={{ padding: '5px 12px', fontSize: '0.82rem', textDecoration: 'none' }}
              >
                ↗️ मूळ उघडा
              </a>
              <button
                type="button"
                className="admin-media-modal-close"
                onClick={() => setActiveMediaModal(null)}
                aria-label="बंद करा"
              >
                ✕
              </button>
            </div>
          </div>
          <div className="admin-media-modal-body">
            {activeMediaModal.isVideo ? (
              <video src={activeMediaModal.url} controls autoPlay style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 8 }} />
            ) : activeMediaModal.isPdf ? (
              <iframe src={activeMediaModal.url} title={activeMediaModal.name} style={{ width: '100%', height: '70vh', border: 0 }} />
            ) : (
              <img src={activeMediaModal.url} alt={activeMediaModal.name} style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 8 }} />
            )}
          </div>
        </div>
      </div>
    )}
  </div>;
}
